import React, { memo, useMemo } from 'react';

// Y positions for notes on the treble staff (offset by STAFF_OFFSET_Y = 120)
const TREBLE_NOTE_POSITIONS = {
  'C6': 89, 'B5': 101, 'A5': 112, 'G5': 124, 'F5': 135, 'E5': 147,
  'D5': 158, 'C5': 169, 'B4': 181, 'A4': 192, 'G4': 204, 'F4': 215,
  'E4': 226, 'D4': 237, 'C4': 248,
};

const BASS_NOTE_POSITIONS = {
  'C4': 307, 'B3': 318, 'A3': 329, 'G3': 340, 'F3': 351, 'E3': 362,
  'D3': 374, 'C3': 385, 'B2': 396, 'A2': 407, 'G2': 419, 'F2': 430,
  'E2': 442, 'D2': 453, 'C2': 465,
};

// Notes that need ledger lines (treble clef)
const TREBLE_LEDGER_NOTES = {
  'A5': [112], 'B5': [112], 'C6': [112, 89],
};
const TREBLE_BELOW_LEDGER_NOTES = { 'C4': [248] };

// Notes that need ledger lines (bass clef)
const BASS_LEDGER_NOTES = { 'E2': [442], 'D2': [442], 'C2': [442, 465] };
const BASS_ABOVE_LEDGER_NOTES = { 'C4': [307] };

// SVG dimensions
const STAFF_WIDTH = 380;
const STAFF_HEIGHT = 500;
const STAFF_OFFSET_Y = 120;

// Whole note SVG path (regular)
const NOTE_PATH = "M14.3132 0.0158635C18.767 -0.163023 23.7767 1.16748 27.1638 4.18676C30.4302 7.0987 31.1138 11.9893 27.9558 15.2659C24.6972 18.6473 20.2062 19.8301 15.695 19.9993C10.8759 20.0354 6.30404 18.9304 2.49775 15.6946C1.07763 14.4513 0.125624 12.4771 0.014353 10.5881C-0.332819 4.68904 5.68691 1.42995 10.6013 0.42602C11.8239 0.176323 13.0747 0.0868781 14.3132 0.0158635ZM18.0563 3.99047C15.6317 1.85688 11.6489 0.0381614 8.84052 2.52563C7.94132 3.3222 7.32901 4.58428 7.26435 5.80102C7.13921 9.0244 8.3767 11.8073 10.281 14.2707C12.1311 16.5237 14.4791 18.3701 17.4421 18.7219C20.3136 19.0631 22.488 16.7969 22.6628 13.9778C22.8438 11.0547 21.5471 7.95823 19.7145 5.73852C19.1943 5.12949 18.6596 4.52138 18.0563 3.99047Z";

// Whole note SVG path (with ledger line extension)
const NOTE_LINE_PATH = "M21.3117 0.0158541C25.7655 -0.162975 30.7753 1.16752 34.1623 4.18675C37.4286 7.09869 38.1113 11.9893 34.9533 15.2659C31.6949 18.6472 27.2047 19.8301 22.6936 19.9993C17.8743 20.0354 13.3017 18.9305 9.49534 15.6946C8.07545 14.4513 7.1242 12.4778 7.01292 10.5891C6.66541 4.68981 12.6844 1.431 17.5989 0.426987C18.8217 0.177229 20.0731 0.0868766 21.3117 0.0158541ZM25.0539 3.99144C22.6292 1.85778 18.6465 0.0387965 15.8381 2.5266C14.939 3.32319 14.3266 4.58534 14.2619 5.80199C14.1369 9.02531 15.3752 11.8083 17.2795 14.2717C19.1297 16.5247 21.4776 18.3702 24.4407 18.7219C27.312 19.0629 29.4854 16.7977 29.6604 13.9787C29.8415 11.0556 28.5447 7.95925 26.7121 5.73949C26.1918 5.13046 25.6572 4.52234 25.0539 3.99144Z";

// Get position for a note based on clef
function getNotePosition(note, clef) {
  if (clef === 'treble') {
    return TREBLE_NOTE_POSITIONS[note];
  } else {
    return BASS_NOTE_POSITIONS[note];
  }
}

// Get ledger lines for a note based on clef
function getLedgerLines(note, clef) {
  if (clef === 'treble') {
    return TREBLE_LEDGER_NOTES[note] || TREBLE_BELOW_LEDGER_NOTES[note] || [];
  } else {
    return BASS_LEDGER_NOTES[note] || BASS_ABOVE_LEDGER_NOTES[note] || [];
  }
}

// Check if note needs the extended ledger line path
function needsLedgerLinePath(note) {
  return note === 'C4' || note === 'A5' || note === 'C6' || note === 'E2' || note === 'C2';
}

// Component for rendering a single note
const NoteGraphic = memo(function NoteGraphic({ noteX, notePosition, note, clef }) {
  const ledgerLines = getLedgerLines(note, clef);
  const isOnLedgerLine = needsLedgerLinePath(note);
  
  const noteTransform = isOnLedgerLine 
    ? `translate(${noteX - 22}, ${notePosition - 10})`
    : `translate(${noteX - 15}, ${notePosition - 10})`;
  
  const notePath = isOnLedgerLine ? NOTE_LINE_PATH : NOTE_PATH;

  // Build ledger line segments
  const ledgerPathD = ledgerLines.length > 0
    ? ledgerLines.map(lineY => `M ${noteX - 30} ${lineY} L ${noteX + 30} ${lineY}`).join(' ')
    : `M 0 0`;

  return (
    <g className="note-group">
      <path
        className="note-oval"
        d={notePath}
        fill="#2B251F"
        fillRule="evenodd"
        transform={noteTransform}
      />
      <path className="ledger-lines" d={ledgerPathD} stroke="#504C48" strokeWidth="2" fill="none" />
    </g>
  );
});

const IntervalStaff = memo(function IntervalStaff({ lowerNote, upperNote, showAnswer = false, correctInterval }) {
  // Determine clef - use treble for notes C4 and above, bass for notes below C4
  const clef = useMemo(() => {
    // If lower note is C4 or higher, use treble clef
    const lowerOctave = parseInt(lowerNote.slice(-1));
    if (lowerOctave >= 4) {
      return 'treble';
    }
    return 'bass';
  }, [lowerNote]);
  
  // Get positions for both notes using the same clef
  const lowerPosition = useMemo(() => {
    const pos = getNotePosition(lowerNote, clef);
    if (pos === undefined) {
      console.warn(`No position found for ${lowerNote} in ${clef} clef`);
      return clef === 'treble' ? 257 : 350; // Default to C4 position
    }
    return pos;
  }, [lowerNote, clef]);

  const upperPosition = useMemo(() => {
    const pos = getNotePosition(upperNote, clef);
    if (pos === undefined) {
      console.warn(`No position found for ${upperNote} in ${clef} clef`);
      return clef === 'treble' ? 200 : 330; // Default position
    }
    return pos;
  }, [upperNote, clef]);

  // Calculate if notes are adjacent (2nd interval) - they need horizontal offset to avoid overlap
  const isAdjacentInterval = useMemo(() => {
    const diff = Math.abs(lowerPosition - upperPosition);
    return diff <= 15; // Notes within 15px are considered adjacent (2nds)
  }, [lowerPosition, upperPosition]);

  // Position notes - offset horizontally for 2nds to avoid overlap
  const baseNoteX = 220;
  const lowerNoteX = isAdjacentInterval ? baseNoteX - 12 : baseNoteX;
  const upperNoteX = isAdjacentInterval ? baseNoteX + 12 : baseNoteX;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center overflow-visible" data-testid="interval-staff">
      <div 
        className="relative rounded-card transition-all duration-300 overflow-visible"
        style={{ maxWidth: '100%', width: 'auto' }}
      >
        <svg 
          viewBox={`0 0 ${STAFF_WIDTH} ${STAFF_HEIGHT}`}
          className="w-full h-auto max-h-[280px] sm:max-h-[320px] md:max-h-[380px]"
          style={{ minWidth: '250px', maxWidth: '420px' }}
          aria-label={`Interval between ${lowerNote} and ${upperNote}`}
          role="img"
        >
          {/* Staff background with clefs and brace */}
          <image 
            href="/note-staff.svg" 
            x="0" 
            y={STAFF_OFFSET_Y} 
            width={STAFF_WIDTH} 
            height="300"
          />
          
          {/* Lower note */}
          <NoteGraphic 
            noteX={lowerNoteX} 
            notePosition={lowerPosition} 
            note={lowerNote}
            clef={clef}
          />
          
          {/* Upper note */}
          <NoteGraphic 
            noteX={upperNoteX} 
            notePosition={upperPosition} 
            note={upperNote}
            clef={clef}
          />
        </svg>
      </div>
      
      {/* Answer reveal */}
      {showAnswer && (
        <div 
          className="mt-6 note-label-reveal"
          aria-live="polite"
        >
          <span className="bg-cream-900 text-white px-6 py-3 rounded-full text-xl font-serif tracking-wide shadow-lg">
            {correctInterval}
          </span>
        </div>
      )}
    </div>
  );
});

export default IntervalStaff;
